/**
 * Q93SubQuestions.tsx — CP9 Paper Process sub-questions (Q9.3.A–D)
 * Locked: S-CP9-1, Fri May 15 2026 2:09 PM EDT
 */

import { AlertTriangle, Ban, CheckCircle2 } from "lucide-react";
import type { QuestionDef, QuestionOption } from "@shared/questions";
import {
  Q93_SUBQ_KEYS,
  resolveQ93Flag,
  summarizeQ93,
  type FlagType,
  type Q93SubqKey,
} from "@/lib/q93_soft_stop_logic";

const AMBER_NOTICE: Record<string, string> = {
  cp9_paper_procurement_contact:
    "Role identified but not named. Confirm the specific contact name before paper closes.",
  cp9_security_it_review:
    "Security/IT review is pending — not yet scheduled. Confirm a date before close.",
  cp9_signing_authority:
    "Role known but name not yet confirmed. Identify the specific signer before close.",
};

const SOFT_STOP_NOTICE: Record<string, string> = {
  cp9_paper_procurement_contact:
    "Procurement contact not identified. Name the contact before paper can advance. Manager override required to proceed. (RULE-ESC-1 eligible)",
  cp9_redline_cycle_status:
    "Redline cycle has not started. Contract cannot close without a redline cycle. Manager override required to proceed. (RULE-ESC-1 eligible)",
  cp9_signing_authority:
    "Signing authority is unclear. The deal cannot close without a named signer. Manager override required to proceed. (RULE-ESC-1 eligible)",
};

const SUBQ_LABEL_BY_KEY: Record<Q93SubqKey, string> = {
  cp9_paper_procurement_contact: "Q9.3.A",
  cp9_redline_cycle_status: "Q9.3.B",
  cp9_security_it_review: "Q9.3.C",
  cp9_signing_authority: "Q9.3.D",
};

function optionStyle(flag: FlagType, isSel: boolean): React.CSSProperties {
  const borderColor =
    flag === "OK" ? "#16A34A" : flag === "AMBER" ? "#EAB308" : "#DC2626";
  const selBg =
    flag === "OK" ? "rgba(22,163,74,0.10)" : flag === "AMBER" ? "rgba(234,179,8,0.12)" : "rgba(220,38,38,0.10)";
  return {
    textAlign: "left",
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
    padding: "10px 14px 10px 16px",
    borderRadius: 8,
    border: isSel ? `2px solid ${borderColor}` : "1px solid #E5E5E7",
    borderLeft: `4px solid ${borderColor}`,
    background: isSel ? selBg : "#FFFFFF",
    color: "#0B0B0F",
    fontSize: 14,
    lineHeight: 1.4,
    cursor: "pointer",
    transition: "background 0.1s, border-color 0.1s",
    width: "100%",
  };
}

function flagBadge(flag: FlagType): React.ReactNode {
  const base: React.CSSProperties = {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    padding: "1px 6px",
    borderRadius: 999,
    color: "#FFFFFF",
    fontSize: 10,
    fontWeight: 700,
    letterSpacing: "0.02em",
  };
  if (flag === "OK") {
    return (
      <span style={{ ...base, background: "#16A34A" }}>
        <CheckCircle2 size={10} />
        OK
      </span>
    );
  }
  if (flag === "AMBER") {
    return (
      <span style={{ ...base, background: "#EAB308" }}>
        <AlertTriangle size={10} />
        AMBER
      </span>
    );
  }
  return (
    <span style={{ ...base, background: "#DC2626" }}>
      <Ban size={10} />
      SOFT STOP
    </span>
  );
}

interface SubRowProps {
  question: QuestionDef;
  selectedKey: string | null;
  onSelect: (optionKey: string | null) => void;
}

function SubRow({ question, selectedKey, onSelect }: SubRowProps) {
  const flag = resolveQ93Flag(question.key, selectedKey);
  const subId = SUBQ_LABEL_BY_KEY[question.key as Q93SubqKey] ?? "";
  const headerBg =
    flag === "SOFT_STOP"
      ? "rgba(220,38,38,0.10)"
      : flag === "AMBER"
        ? "rgba(234,179,8,0.12)"
        : flag === "OK"
          ? "rgba(22,163,74,0.10)"
          : "#FAFAF7";

  return (
    <div
      data-testid={`subquestion-${question.key}`}
      style={{
        border: "1px solid #E5E5E7",
        borderRadius: 8,
        background: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          padding: "8px 12px",
          background: headerBg,
          borderBottom: "1px solid #E5E5E7",
        }}
      >
        <span
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 11,
            fontWeight: 700,
            color: "#525258",
            letterSpacing: "0.04em",
          }}
        >
          {subId}
        </span>
        <span
          style={{ fontSize: 14, fontWeight: 700, color: "#0B0B0F", flex: 1 }}
        >
          {question.label}
        </span>
        {flag && flagBadge(flag)}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 6,
          padding: "10px 12px",
        }}
        role="radiogroup"
        aria-label={question.label}
      >
        {question.helpText && (
          <div
            style={{
              fontSize: 12,
              color: "#525258",
              marginBottom: 4,
              lineHeight: 1.45,
            }}
          >
            {question.helpText}
          </div>
        )}
        {question.options.map((opt: QuestionOption) => {
          const isSel = selectedKey === opt.key;
          return (
            <button
              key={opt.key}
              type="button"
              role="radio"
              aria-checked={isSel}
              data-testid={`answer-${question.key}-${opt.key}`}
              onClick={() => onSelect(isSel ? null : opt.key)}
              style={optionStyle(opt.flag as FlagType, isSel)}
              onMouseEnter={(e) => {
                if (!isSel)
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "#FAFAF7";
              }}
              onMouseLeave={(e) => {
                if (!isSel)
                  (e.currentTarget as HTMLButtonElement).style.background =
                    "#FFFFFF";
              }}
            >
              <span style={{ fontWeight: isSel ? 700 : 500 }}>{opt.label}</span>
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 700,
                  color: "#6B6B70",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                {opt.points} {opt.points === 1 ? "pt" : "pts"}
              </span>
            </button>
          );
        })}

        {flag === "SOFT_STOP" && SOFT_STOP_NOTICE[question.key] && (
          <div
            role="alert"
            data-testid={`soft-stop-banner-${question.key}`}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              padding: "8px 10px",
              marginTop: 4,
              borderRadius: 6,
              background: "rgba(220,38,38,0.10)",
              border: "1px solid #DC2626",
              color: "#0B0B0F",
              fontSize: 12,
              lineHeight: 1.4,
            }}
          >
            <Ban size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>
              <strong>SOFT STOP — {subId}.</strong>{" "}
              {SOFT_STOP_NOTICE[question.key]}
            </span>
          </div>
        )}
        {flag === "AMBER" && AMBER_NOTICE[question.key] && (
          <div
            data-testid={`amber-notice-${question.key}`}
            style={{
              display: "flex",
              alignItems: "flex-start",
              gap: 8,
              padding: "8px 10px",
              marginTop: 4,
              borderRadius: 6,
              background: "rgba(234,179,8,0.12)",
              border: "1px solid #EAB308",
              color: "#0B0B0F",
              fontSize: 12,
              lineHeight: 1.4,
            }}
          >
            <AlertTriangle size={14} style={{ flexShrink: 0, marginTop: 1 }} />
            <span>{AMBER_NOTICE[question.key]}</span>
          </div>
        )}
      </div>
    </div>
  );
}

interface Q93SubQuestionsProps {
  questions: QuestionDef[];
  selectedByKey: Record<string, string | null>;
  onSelect: (questionKey: string, optionKey: string | null) => void;
}

export default function Q93SubQuestions({
  questions,
  selectedByKey,
  onSelect,
}: Q93SubQuestionsProps) {
  const summary = summarizeQ93({
    cp9_paper_procurement_contact:
      selectedByKey.cp9_paper_procurement_contact ?? null,
    cp9_redline_cycle_status: selectedByKey.cp9_redline_cycle_status ?? null,
    cp9_security_it_review: selectedByKey.cp9_security_it_review ?? null,
    cp9_signing_authority: selectedByKey.cp9_signing_authority ?? null,
  });

  const headerTint = summary.hasSoftStop
    ? "rgba(220,38,38,0.10)"
    : summary.hasAmber
      ? "rgba(234,179,8,0.12)"
      : summary.allAnswered
        ? "rgba(22,163,74,0.10)"
        : "#FAFAF7";
  const headerBorder = summary.hasSoftStop
    ? "#DC2626"
    : summary.hasAmber
      ? "#EAB308"
      : summary.allAnswered
        ? "#16A34A"
        : "#E5E5E7";

  const byKey = new Map(questions.map((q) => [q.key, q]));

  return (
    <div
      data-testid="q93-subquestions-group"
      style={{
        border: "1px solid #E5E5E7",
        borderRadius: 10,
        overflow: "hidden",
        background: "#FFFFFF",
        marginBottom: 4,
      }}
    >
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 10,
          padding: "10px 14px",
          background: headerTint,
          borderBottom: `1px solid ${headerBorder}`,
        }}
      >
        <span
          style={{
            fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace",
            fontSize: 10,
            fontWeight: 800,
            color: "#525258",
            letterSpacing: "0.12em",
            textTransform: "uppercase",
          }}
        >
          Paper Process
        </span>
        <span style={{ fontSize: 12, color: "#525258" }}>
          Q9.3.A–D · grouped sub-questions
        </span>
        <span style={{ flex: 1 }} />
        {summary.hasSoftStop && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 8px",
              borderRadius: 999,
              background: "#DC2626",
              color: "#FFFFFF",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            <Ban size={11} />
            SOFT STOP present
          </span>
        )}
        {!summary.hasSoftStop && summary.hasAmber && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 8px",
              borderRadius: 999,
              background: "#EAB308",
              color: "#FFFFFF",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            <AlertTriangle size={11} />
            AMBER present
          </span>
        )}
        {!summary.hasSoftStop && !summary.hasAmber && summary.allAnswered && (
          <span
            style={{
              display: "inline-flex",
              alignItems: "center",
              gap: 4,
              padding: "2px 8px",
              borderRadius: 999,
              background: "#16A34A",
              color: "#FFFFFF",
              fontSize: 10,
              fontWeight: 700,
            }}
          >
            <CheckCircle2 size={11} />
            All OK
          </span>
        )}
      </div>

      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 10,
          padding: 12,
          background: "#FAFAF7",
        }}
      >
        {Q93_SUBQ_KEYS.map((k) => {
          const q = byKey.get(k);
          if (!q) return null;
          return (
            <SubRow
              key={k}
              question={q}
              selectedKey={selectedByKey[k] ?? null}
              onSelect={(opt) => onSelect(k, opt)}
            />
          );
        })}
      </div>
    </div>
  );
}
